import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IBrandInfo } from '../../interfaces/brand/brand-info';
import { SharedService } from '../../services/shared.service';
import { DataExchangeService } from '../../services/session/data-exchange.service';
import { ComponentSectionFactoryService } from '../../services/component-section-factory.service';
import { HeaderSectionDirective } from '../../directives/header-section.directive';
import { FooterSectionDirective } from '../../directives/footer-section.directive';
import { BodySectionDirective } from '../../directives/body-section.directive';
import { ThemeDirective } from '../../directives/theme.directive';
import { NGXLogger } from 'ngx-logger';

@Component({
    selector: 'app-page-container',
    standalone: true,
    templateUrl: './page-container.component.html',
    styleUrl: './page-container.component.css',
    imports: [ThemeDirective, HeaderSectionDirective, BodySectionDirective, FooterSectionDirective, CommonModule]
})
export class PageContainerComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild(HeaderSectionDirective, {static: true}) headerComponentSection!: HeaderSectionDirective;
  @ViewChild(FooterSectionDirective, {static: true}) footerComponentSection!: FooterSectionDirective;
  @ViewChild(BodySectionDirective, {static: true}) bodyComponentSection!: BodySectionDirective;
  @ViewChild('panelHeading') panelHeading!: ElementRef<HTMLElement>;
  @ViewChild('panelContent') panelContent!: ElementRef<HTMLElement>;

  private readonly sharedService = inject(SharedService);
  public readonly dataExchangeService = inject(DataExchangeService);
  public readonly componentSectionFactoryService = inject(ComponentSectionFactoryService);
  private readonly logger = inject(NGXLogger);

  brandStyleTheme: any = {};

  defaultImageUrl : string = '../../../assets/images/wtu_background.png';

  isPageEnhanced: boolean = false;
  isMobileMode: boolean = false;
  isHeaderFrozen: boolean = false;
  // isNarrowLayout: boolean = false;

  backgroundImageUrl!: string;

  private resizeObserver?: ResizeObserver;

  constructor() {
  }

  ngOnInit() {
    this.logger.debug('page-container - entered ngOnInit', new Date().toISOString());
    let pageSource: string = this.dataExchangeService.getSource();
    let brandInfo: IBrandInfo = this.dataExchangeService.getBrandInfo();
    // let brandImageLogo: string = this.dataExchangeService.getBrandLogoImage();
    // let nonmobileImage: string = this.dataExchangeService.getNonmobileImage();
    //           console.log('page container - brandInfo', brandInfo);
    //           console.log('page container - brandImageLogo', brandImageLogo);
    if (brandInfo) {
      this.brandStyleTheme = this.sharedService.getBrandStyleScheme(brandInfo);
      // Additional parameters
      const additionalParams: Map<string,any> = new Map<string,any>();

      // additionalParams.set('brandImageLogo', brandImageLogo);
      // additionalParams.set('nonmobileImage', nonmobileImage);
      if (pageSource == 'nm-page') {
        additionalParams.set('useNonmobileBrandImage', true);
      }
      // Create form header and footer
      this.makeHeaderAndFooter(brandInfo, additionalParams);
      // Create section for the body of the component
      setTimeout(() => this.makeBodySection(brandInfo, pageSource), 0);
    }
  }

  ngAfterViewInit() {
    // Sync scrollable content margin-top to the actual rendered header height.
    // isHeaderFrozen is set asynchronously (inside setTimeout → makeBodySection),
    // so we observe unconditionally and only apply the margin when frozen.
    this.resizeObserver = new ResizeObserver(() => {
      if (!this.isHeaderFrozen) return;
      const headerHeight = this.panelHeading.nativeElement.offsetHeight;
      this.panelContent.nativeElement.style.marginTop = `${headerHeight}px`;
    });
    this.resizeObserver.observe(this.panelHeading.nativeElement);
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
  }

  makeHeaderAndFooter(brandInfo: IBrandInfo, additionalParams: Map<string,any>) {
        this.logger.debug('page-container - makeHeaderAndFooter - before', new Date().toISOString());
    this.componentSectionFactoryService.loadHeaderSection(brandInfo, this.headerComponentSection, additionalParams);
    this.componentSectionFactoryService.loadFooterSection(brandInfo, this.footerComponentSection);
        this.logger.debug('page-container - makeHeaderAndFooter - after', new Date().toISOString());
  }

  makeBodySection(brandInfo: IBrandInfo, pageSource: string) {
        this.logger.debug('page-container - makeBodySection - pageSource', pageSource, new Date().toISOString());
    switch (pageSource) {
      case 'aw-recov':
        this.isMobileMode = true;
        this.componentSectionFactoryService.loadBodySection(brandInfo, "WAL_PAGES_APPLE_RECOVERY_SECTION_TYPE", this.bodyComponentSection);
        break;
      case 'ev-page':
        this.isMobileMode = true;
        // this.isNarrowLayout = true;
        this.logger.debug('page-container - makeBodySection - before', new Date().toISOString());
        this.componentSectionFactoryService.loadBodySection(brandInfo, "WAL_PAGES_EV_SECTION_TYPE", this.bodyComponentSection);
        this.logger.debug('page-container - makeBodySection - after', new Date().toISOString());
        break;
      case 'msg-page':
        this.isMobileMode = false;
        // this.isNarrowLayout = true;
        this.logger.debug('page-container - makeBodySection - before', new Date().toISOString());
        this.componentSectionFactoryService.loadBodySection(brandInfo, "WAL_PAGES_MSG_SECTION_TYPE", this.bodyComponentSection);
        this.logger.debug('page-container - makeBodySection - after', new Date().toISOString());
        break;
      case 'nm-page':
        this.isMobileMode = false;
        if (this.sharedService.getBrandConfigProperty(brandInfo, 'WAL_PAGES_NONMOBILE_SECTION_TYPE') == 'nonmobile-enhanced') {
          this.backgroundImageUrl = this.sharedService.getBrandConfigProperty(
            brandInfo, 'WAL_PAGES_NONMOBILE_BACKGROUND_IMAGE_URL', this.defaultImageUrl);
          this.isPageEnhanced = true;
        }
        this.componentSectionFactoryService.loadBodySection(brandInfo, "WAL_PAGES_NONMOBILE_SECTION_TYPE", this.bodyComponentSection);
        break;
      case 'notif-log':
        this.isMobileMode = true;
        this.componentSectionFactoryService.loadBodySection(brandInfo, "WAL_PAGES_NOTIF_LOG_SECTION_TYPE", this.bodyComponentSection);
        if (this.sharedService.getBrandConfigProperty(brandInfo, 'WAL_PAGES_NOTIF_LOG_SECTION_TYPE') == 'notif-log-card-deck'
          || this.sharedService.getBrandConfigProperty(brandInfo, 'WAL_PAGES_NOTIF_LOG_SECTION_TYPE') == 'notif-log-grid') {
            this.isHeaderFrozen = true;
        }
        break;
      case 'sm-link':
        this.isMobileMode = false;
        if (this.sharedService.getBrandConfigProperty(brandInfo, 'WAL_PAGES_SMART_LINK_SECTION_TYPE') == 'smart-link-enhanced') {
          this.backgroundImageUrl = this.sharedService.getBrandConfigProperty(
            brandInfo, 'WAL_PAGES_SMART_LINK_BACKGROUND_IMAGE_URL', this.defaultImageUrl);
          this.isPageEnhanced = true;
        }
        this.componentSectionFactoryService.loadBodySection(brandInfo, "WAL_PAGES_SMART_LINK_SECTION_TYPE", this.bodyComponentSection);
        break;
      case 'sub-accts':
        this.isMobileMode = true;
        this.componentSectionFactoryService.loadBodySection(brandInfo, "WAL_PAGES_SUB_ACCOUNTS_SECTION_TYPE", this.bodyComponentSection);
        if (this.sharedService.getBrandConfigProperty(brandInfo, 'WAL_PAGES_SUB_ACCOUNTS_SECTION_TYPE') == 'subaccounts-card-deck') {
          this.isHeaderFrozen = true;
        }
        break;
      case 'xtr-vi':
        this.isMobileMode = true;
        this.componentSectionFactoryService.loadBodySection(brandInfo, "WAL_PAGES_VIEW_ITEMS_SECTION_TYPE", this.bodyComponentSection);
        break;
    }
  }

  getClasses() {
    return {
      'col-12': this.isMobileMode,
      'col-md-3': this.isMobileMode,
      'msg-container': this.isMobileMode,
      'bg': !this.isMobileMode,
    };
  }

  getStyles() {
    if (this.isMobileMode) {
      return {
        'background-image': 'none',
        'background-size': 'auto',
        'min-height': '0',
      };
    }

    // Non-mobile (enhanced) page: the background image must cover the entire
    // page width. This div is a flex item of .page-center-wrapper, so without
    // an explicit width it shrinks to its content and the background would only
    // span the card. Stretch it full width and center the card inside it.
    return {
      'background-image': this.isPageEnhanced ? 'url(' + this.backgroundImageUrl + ')' : 'none',
      'background-size': 'cover',
      'background-position': 'center center',
      'background-repeat': 'no-repeat',
      'min-height': '100vh',
      'width': '100%',
      'display': 'flex',
      'justify-content': 'center',
      'align-items': 'flex-start',
    };
  }

  // getStyles() {
  //   return {
  //     'background-image': this.isPageEnhanced && !this.isMobileMode ? `url(${this.backgroundImageUrl})` : 'none',
  //     'background-size': 'cover',
  //     'background-repeat': 'no-repeat',
  //     'background-position': 'center center',
  //     'width': '100vw',
  //     'height': '100vh',
  //     'display': !this.isMobileMode ? 'flex' : 'block',
  //     'justify-content': !this.isMobileMode ? 'center' : '',
  //     'align-items': !this.isMobileMode ? 'center' : '',
  //     'overflow': 'hidden'
  //   };
  // }

}
